import styled from '@emotion/styled'

import { themedValue } from '../../__styling/themed-value'
import { metrics } from '../../__styling/settings/metrics'
import { colors } from '../../__styling/settings/colors'
import {
    breakpoint,
    breakpointMax,
    breakpointBetween,
} from '../../__styling/style-functions'
import { supports } from '../../__helpers/at-supports-helper'
import {
    horizontalDividers,
    verticalDividers,
} from '../../__styling/style-mixins'
import { ThemeMargins } from '../../__styling/settings/metrics'
import { calcRem } from '../../__styling/style-functions/calc-rem'

export interface GridProps {
    verticalSpacing: keyof ThemeMargins | undefined
}

export const StyledUniform = styled('div')<GridProps>(
    ({ theme, verticalSpacing }) => [
        {
            display: 'flex',
            position: 'relative',
            width: '100%',
            margin: '0 auto',
            flexWrap: 'wrap',
            marginBottom:
                verticalSpacing &&
                themedValue(theme, {
                    thewest: calcRem(theme.margins[verticalSpacing]),
                    fallback: undefined,
                }),
        },
        // Dividers and borders for the west
        theme.kind === 'thewest' && {
            paddingBottom: metrics.thewest.margins.sm,
            borderBottom: `4px double ${colors.thewest.greyGorilla}`,
        },
        supports('display: grid', {
            display: 'grid',
            gridGap: theme.margins.gridGap,
            gridTemplateColumns: '1fr',

            [breakpoint('sm')]: {
                gridTemplateColumns: 'repeat(2, 1fr)',
            },

            [breakpoint('md')]: {
                gridTemplateColumns: 'repeat(4, 1fr)',
            },
        }),
    ],
)
StyledUniform.displayName = 'StyledUniform'

export const StyledUniformCardcontainer = styled('div')(
    {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        flexGrow: 1,
        flexShrink: 1,
        flexWrap: 'wrap',
        width: '100%',
    },
    ({ theme }) => [
        {
            marginBottom: themedValue(theme, {
                thewest: calcRem(theme.margins.gridGap),
                sevennews: calcRem(metrics.sevennews.margins.md),
                fallback: undefined,
            }),

            '& > *:not(:last-child)': {
                marginBottom: calcRem(theme.margins.gridGap),
            },

            [breakpoint('sm')]: {
                width: `calc(50% - ${theme.margins.gridGap}px)`,
                marginRight: calcRem(theme.margins.gridGap),

                ':nth-child(2n)': {
                    marginRight: 0,
                },
            },

            [breakpoint('md')]: {
                flexGrow: 1,
                flexShrink: 1,
                width: `calc(25% - ${theme.margins.gridGap}px)`,
                marginRight: calcRem(theme.margins.gridGap),

                ':nth-child(2n)': {
                    marginRight: calcRem(theme.margins.gridGap),
                },

                ':nth-child(4)': {
                    marginRight: 0,
                },
            },
        },

        // Dividers
        theme.kind === 'thewest' && {
            position: 'relative',

            // Horizontal Divider For stacked cards inside a column
            '& > *:not(:last-child)::before': {
                ...horizontalDividers,
            },

            '@media screen and (min-width: 768px)': {
                ':not(:first-child)::after': {
                    ...verticalDividers,
                },
            },

            // Small devices for bottom of columns
            [breakpointMax('sm')]: {
                '&:not(:last-child)::before': {
                    ...horizontalDividers,
                },
            },

            [breakpointBetween('sm', 'md')]: {
                // Add horizontal dividers on the bottom of the first row of cards
                ':nth-child(-n + 2)::before': {
                    ...horizontalDividers,
                },

                // Remove Vertical Dividers so it wont appear above "Medium breakpoint"
                ':nth-child(2n + 1)::after': {
                    content: 'none',
                },
            },
        },

        theme.kind === 'sevennews' && {
            position: 'relative',

            [breakpoint('md')]: {
                width: `calc(25% - ${theme.margins.gridGap}px)`,
                marginRight: theme.margins.gridGap,

                ':nth-child(2n)': {
                    marginRight: calcRem(theme.margins.gridGap),
                },

                ':nth-child(4)': {
                    marginRight: 0,
                },
            },
        },
        supports('display: grid', {
            marginBottom: 0,

            [breakpoint('sm')]: {
                width: 'auto',
                marginRight: 0,

                ':nth-child(2n), :nth-child(4)': {
                    marginRight: 0,
                },
            },
        }),
    ],
)
StyledUniformCardcontainer.displayName = 'UniformCardContainer'

export const StyledHeader = styled('div')(({ theme }) => ({
    display: 'block',
    marginBottom: theme.margins.md,
}))
